import { Event, MapEvent } from "@/apiTypes/event";

export type PriceRange = { min: number; max: number };

/**
 * Takes a number in cents and outputs a decimal currency ammount.
 * e.g. 250 -> $2.50
 * @param cents The price in centrs
 * @param symbol (optional) if true, include the current symbol
 * @returns A formatted price string in decimal USD.
 */
export function formatPrice(
  cents: number,
  {currency = 'USD', symbol = true, precise = false} = {},
): string {
  if (cents === 0) return 'Free';
  if (precise) {
    return new Intl.NumberFormat('en-US', {
      style: symbol ? 'currency' : 'decimal',
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }
  if (symbol) {
    return `$${Math.floor(cents / 100)}`;
  }
  return `${Math.floor(cents / 100)}`;
}

/**
 * Takes an min and max price, and generate a price range string.
 * e.g. 10-100
 * @param range
 * @returns
 */
export function formatPriceRange({ min, max }: PriceRange): string {
  const minPrice = formatPrice(min);
  const maxPrice = formatPrice(max);
  if (min === max) {
    return minPrice;
  }
  return `${minPrice} – ${maxPrice}`;
}

/**
 * Extracts a price range of categories from an Event object.
 *
 * @param event The event to evaluate
 * @returns a PriceRange object with min and max price,
 * or undefined if no prices exist
 */
export function getPriceRange(event: MapEvent | Event): PriceRange | undefined {
  const allPrices =
    event.categories?.flatMap(
      (cat) => cat.prices?.map((p) => p.amount) ?? [],
    ) ?? [];
  if (allPrices.length === 0) return undefined;
  return {
    min: Math.min(...allPrices),
    max: Math.max(...allPrices),
  };
}
